import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  AbstractComponent,
  NavigationService,
  PopoverService,
  SolBreadcrumb,
  SolButtonComponent,
  SolButtonType,
  SolIconComponent,
  SolPopoverMenuContentComponent,
  SolPopoverRef,
  SolRouteId,
  SolTabsComponent,
  SolLoader,
  SolLoaderComponent,
  SolText,
  SolTooltip,
  TranslatePipe,
  SolTooltipComponent,
} from '../../../../public-api';
import { SolIcon } from '@sol/icons';
import { TopbarService } from '../../../services/topbar.service';
import { SolTopbarState } from './sol-topbar.state';

@Component({
  selector: 'sol-topbar',
  imports: [
    SolButtonComponent,
    SolTabsComponent,
    TranslatePipe,
    SolIconComponent,
    SolLoaderComponent,
    NgTemplateOutlet,
    SolTooltipComponent,
  ],
  templateUrl: './sol-topbar.component.html',
})
export class SolTopbarComponent extends AbstractComponent<SolTopbarState> implements AfterViewInit, OnDestroy {
  protected readonly SolButtonType = SolButtonType;
  protected readonly SolIcon = SolIcon;
  protected readonly SolLoader = SolLoader;
  protected readonly backTooltip = SolTooltip.of(SolText.of('sol-common.back'));

  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly topbarService = inject(TopbarService);
  private readonly popoverService = inject(PopoverService);

  // Computed
  protected readonly customBreadcrumbs$ = this.compute(state => state.customBreadcrumbs);
  protected readonly breadcrumbs$ = this.compute(state => state.visibleBreadcrumbs);
  protected readonly breadcrumbTooltips$ = this.compute(state => state.breadcrumbTooltips);
  protected readonly promotedActions$ = this.compute(state => state.promotedActions);
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly breadcrumbsLoading$ = this.compute(state => state.breadcrumbsLoading);
  protected readonly navbarHidden$ = this.compute(state => state.navbarHidden);
  protected readonly tabs$ = this.compute(state => state.tabs);
  protected readonly hasContextMenu$ = this.compute(state => state.hasContextMenu);
  protected readonly selectedTab$ = this.compute(state => state.selectedTab);
  protected readonly toolbarTemplate$ = this.compute(state => state.toolbarTemplate);
  protected readonly breadcrumbsTemplate$ = this.compute(state => state.breadcrumbsTemplate);
  protected readonly collapsed$ = this.compute(state => state.collapsed);
  protected readonly collapsedOpen$ = this.compute(state => state.collapsedOpen);
  protected readonly hasTemplateLeaf$ = this.compute(state => state.hasTemplateLeaf);
  protected readonly leadingBreadcrumbs$ = this.compute(state => state.leadingBreadcrumbs);
  protected readonly leafBreadcrumb$ = this.compute(state => state.leafBreadcrumb);
  protected readonly backBreadcrumb$ = this.compute(state => state.backBreadcrumb);

  // Binding
  @ViewChild('breadcrumbsCell')
  private breadcrumbsCellRef?: ElementRef<HTMLElement>;

  // Cache
  private resizeObserver?: ResizeObserver;
  private measureScheduled = false;
  private naturalWidth = -1;
  private popoverRef?: SolPopoverRef<SolPopoverMenuContentComponent>;

  constructor() {
    super(SolTopbarState.of());

    // Watch navigation info
    this.navigationService.watch(nss => {
      const topbarData = nss.topbarData;
      this.update(state =>
        state.withNavigation(
          nss.activeRouteStatus === 'loading',
          topbarData.breadcrumbs,
          topbarData.pageNamesLoaded,
          topbarData.tabs,
          topbarData.selectedTab,
          !nss.navbarVisible,
        ),
      );
      this.resetMeasure();
    });

    // Watch topbar actions and templates
    this.topbarService.watch(tss => {
      this.update(state => state.withMenu(tss.menu).withTemplates(tss.toolbarTemplate, tss.breadcrumbsTemplate));
      this.resetMeasure();
    });
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    if (this.breadcrumbsCellRef) {
      this.resizeObserver.observe(this.breadcrumbsCellRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.popoverRef?.close();
    this.resizeObserver?.disconnect();
  }

  @HostBinding('class')
  public get hostClass(): string {
    return 'sol-topbar';
  }

  @Input()
  public set customBreadcrumbs(customBreadcrumbs: boolean) {
    this.update(state => state.withCustomBreadcrumbs(customBreadcrumbs));
  }

  /**
   * When true, the topbar is rendered inside a host shell. The first breadcrumb
   * (the embedTabGroup label) is hidden so users see only the nested page crumbs.
   * Threading: app.component.html → asm-topbar → sol-topbar.
   */
  @Input()
  public set embedded(embedded: boolean) {
    this.update(state => state.withEmbedded(embedded));
  }

  protected onBreadcrumbClick(breadcrumb: SolBreadcrumb): void {
    if (breadcrumb.clickable) {
      this.update(state => state.withCollapsedOpen(false));
      this.navigationService.navigate(breadcrumb.id, { restoreQueryParams: true });
    }
  }

  protected onToggleCollapsed(): void {
    this.update(state => state.withCollapsedOpen(!state.collapsedOpen));
  }

  /**
   * Closes the collapsed popover on any outside click. Clicks inside the topbar or inside a SOL
   * popover overlay (the scope/subscope dropdowns folded into the popover) are left open.
   */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.state.collapsedOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.sol-topbar') || target?.closest('.sol-popovers-container')) {
      return;
    }
    this.update(state => state.withCollapsedOpen(false));
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.state.collapsedOpen) {
      this.update(state => state.withCollapsedOpen(false));
    }
  }

  /**
   * Forgets the measured natural width and re-measures, so a content change re-renders the trail
   * fully for one pass before deciding whether to collapse again.
   */
  private resetMeasure(): void {
    this.naturalWidth = -1;
    this.scheduleMeasure();
  }

  /** Coalesces measurements into a single animation frame. */
  private scheduleMeasure(): void {
    if (this.measureScheduled) {
      return;
    }
    this.measureScheduled = true;
    requestAnimationFrame(() => {
      this.measureScheduled = false;
      this.measure();
    });
  }

  /**
   * Measures the cell against the trail's natural width and only updates state when the collapsed
   * state actually flips, so a resize that stays on one side of the threshold causes no re-render.
   * While collapsed the cell holds only the button and leaf, so its scrollWidth is not the natural
   * width — the frozen value is reused, and the trail re-expands only once the cell grows past it.
   */
  private measure(): void {
    const cell = this.breadcrumbsCellRef?.nativeElement;
    if (!cell) {
      return;
    }
    const containerWidth = cell.clientWidth;
    if (!this.state.collapsed) {
      this.naturalWidth = cell.scrollWidth;
    }
    const collapsed = this.naturalWidth >= 0 && containerWidth > 0 && this.naturalWidth > containerWidth + 1;
    if (collapsed !== this.state.collapsed) {
      this.update(state => state.withCollapsed(collapsed));
    }
  }

  protected onAction(actionId: string) {
    this.topbarService.onAction(actionId);
    return true;
  }

  protected onSelectedTab(routeId: string): void {
    this.navigationService.navigate(routeId as SolRouteId);
  }

  /**
   * The way back to a hidden navbar. Opens only: the drawer lands on top of this button, so
   * closing is the pointer moving off the navbar, or its own toggle docking it again.
   */
  protected onNavbarOpen(): void {
    this.navigationService.updateNavbarDrawerOpen(true);
  }

  protected showMenu(event: MouseEvent) {
    const menu = this.state.contextActions;
    this.popoverRef = this.popoverService.showMenu(menu, name => this.onAction(name), {
      boundsEvent: event,
    });
  }
}
